import { useEffect, useState } from 'react';
import { useCatchAllConfirmModal } from '../commentGuards/use-catch-all-confirm-modal';
import { useSelector } from 'react-redux';
import { selectInstagramCommentGuards } from '@mm/state/selectors/promoterSelector';
import { StoreState } from '@mm/state/store-interfaces';
import { selectChatStarterRecordsByUiFilterKind } from '@mm/state/selectors/chatStarters';
import {
  activeChatbotSelector,
  chatbotIsActiveOnConnectedPageSelector,
} from '@mm/state/selectors/viewSelectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { useShowInactiveBannerFlag } from '../../../common/hooks/use-show-inactive-bot-banner-flag';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { PromoterModel } from '@mm/state/model/PromoterModel';

export const useActivateIgCommentGuard = (promoter?: PromoterModel) => {
  const chatbot = useSelector(activeChatbotSelector);
  const chatbotIsActiveOnConnectedPage = useSelector(
    chatbotIsActiveOnConnectedPageSelector,
  );
  const { openActivateInactiveBotModal } = useModalActions();
  const { onClickActivate } = useShowInactiveBannerFlag();
  const instagramCGStarters = useSelector((state: StoreState) =>
    selectChatStarterRecordsByUiFilterKind(state, 'instagram_comment_guard'),
  );
  const promoterCRUDActions = usePromoterCRUDActions();

  const instagramPromoters = useSelector(selectInstagramCommentGuards);
  const [toggler, setToggler] = useState(
    promoter?.keywordsEnabled ? 'keywords' : 'all',
  );
  const [isRemoteCatchAllCGUpdated, setIsRemoteCatchAllCGUpdated] =
    useState(false);
  const [isInitLoading, setIsInitLoading] = useState(true);

  const remoteCatchAllCG = instagramCGStarters?.chatStarters?.find(
    starter =>
      starter.subKind === 'all' &&
      starter.active &&
      (starter.postId === 'ALL' || !starter.postId),
  );

  const openCatchAllConfirmModal = useCatchAllConfirmModal(
    'Important Note',
    `Activating this catch-all comment guard will disable ${remoteCatchAllCG?.name}
    Are you sure you would like to proceed?`,
  );

  const setCatchAll = () => {
    if (remoteCatchAllCG) {
      const instagramCatchAllPromoterToDeactivate = instagramPromoters?.find(
        promoter => promoter.id === remoteCatchAllCG.id && promoter.active,
      );

      if (instagramCatchAllPromoterToDeactivate) {
        promoterCRUDActions.update(chatbot.id, {
          ...instagramCatchAllPromoterToDeactivate,
          active: false,
        });
      }
    }
  };

  const inactiveAllGuard =
    (promoter?.postId === 'ALL' || !promoter?.postId) &&
    !promoter?.keywordsEnabled &&
    !promoter?.active;

  const showCatchAllModal =
    remoteCatchAllCG &&
    remoteCatchAllCG.id !== promoter?.id &&
    inactiveAllGuard;

  const onModalClose = (ok: boolean) => {
    if (ok) {
      setCatchAll();
      setIsRemoteCatchAllCGUpdated(true);
    }
  };

  useEffect(() => {
    if (isInitLoading) {
      setIsInitLoading(false);
    } else if (showCatchAllModal && toggler === 'all') {
      openCatchAllConfirmModal(onModalClose);
    }
  }, [toggler]);

  const updatePromoter = () => {
    if (showCatchAllModal) {
      setCatchAll();
    }

    if (promoter) {
      promoterCRUDActions.update(chatbot.id, {
        ...promoter,
        active: !promoter.active,
      });
    }
  };

  const customActivateAction = () => {
    if (promoter) {
      if (!chatbotIsActiveOnConnectedPage && !promoter.active) {
        openActivateInactiveBotModal({
          onBotActivateConfirm: () =>
            setTimeout(() => onClickActivate(updatePromoter), 500),
          useCustomOnBotActivateConfirm: true,
        });
      } else if (showCatchAllModal && !isRemoteCatchAllCGUpdated) {
        openCatchAllConfirmModal(updatePromoter);
      } else {
        updatePromoter();
      }
    }
  };

  return {
    customActivateAction,
    setToggler,
  };
};
